/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 * Page Reports Constructor component
 *
 * @author Evgeniy Naumov
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.22.0
 */

import { FC, useEffect, useMemo } from 'react';

import InfiniteScroll from 'react-infinite-scroller';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import Title from '@components/common/Title/Title';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import { makeSelectPageReportsConstructorTranslation } from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { Loader } from '@modules/loader/components/Loader';

import { ADDITIONAL_REPORTS_ICONS, NEW_REPORTS_DATA, EConstructorItem } from '../constants';
import { reportsConstructorTemplatesCountSelector, reportsConstructorTemplatesPageSelector, reportsConstructorTemplatesSelector } from '../selector';
import { useGetReportConstructorMetaQuery, useLazyGetConstructorTemplatesQuery } from '../store';
import type { IConstructorReport, IReportsConstructorLocationData } from '../types';
import ConstructorFooter from './ConstructorFooter';
import ConstructorHeader from './ConstructorHeader';
import { ConstructorItemsList } from './ConstructorItemsList';
import { ConstructorStyled } from './styled/ConstructorStyled';

const PageReportsConstructor: FC = () => {
    const templates = useSelector(reportsConstructorTemplatesSelector);
    const templatesCount = useSelector(reportsConstructorTemplatesCountSelector);
    const tReportsConstructorTranslation = useSelector(makeSelectPageReportsConstructorTranslation());
    const { common: tConstructorCommon } = tReportsConstructorTranslation;
    const { newReports: tNewReports } = tReportsConstructorTranslation;
    const { additionalReports: tAdditionalReports } = tReportsConstructorTranslation;

   const templatePage = useSelector(reportsConstructorTemplatesPageSelector)

    const { state: locationState } = useLocation<IReportsConstructorLocationData>();

    const {
        data: metaData,
        isSuccess: metaIsLoaded,
        isLoading: metaIsLoading,
    } = useGetReportConstructorMetaQuery({
        bisAccountId: locationState?.bisAccountId,
        source: locationState?.source,
    });

    const [getConstructorTemplates, { isFetching: templatesIsFetching }] = useLazyGetConstructorTemplatesQuery();

    const getConstructorTemplateData = () => {
        if (templatesIsFetching) {
            return;
        }
        getConstructorTemplates(templatePage + 1);
    };

    useEffect(() => {
        getConstructorTemplates(0);
    }, []);

    const additionalReports: IConstructorReport[] = useMemo(() => {
        if (metaData?.additionalReports.length) {
            return metaData.additionalReports.map((el) => ({
                ...el,
                ...ADDITIONAL_REPORTS_ICONS[el.type],
                ...tAdditionalReports[el.type],
            }));
        } else return [];
    }, [metaData?.additionalReports, tAdditionalReports]);

    const newReports: IConstructorReport[] = useMemo(() => {
        return NEW_REPORTS_DATA.map((el) => ({ ...el, ...tNewReports[el.type] }));
    }, [tNewReports]);

    return (
        <Loader showLoader={metaIsLoading}>
            {metaIsLoaded && (
                <ConstructorStyled>
                    <PageLocale lazy fileNames={[TranslationFileNamesEnum.pageReportsConstructor]} />
                    <Title pageName={ECommonTranslationPages.reportsConstructor} />
                    <ConstructorHeader
                        subscribersInBasket={metaData.subscribersCount}
                        translation={tReportsConstructorTranslation}
                    />
                    <ConstructorItemsList items={newReports} title={tConstructorCommon.titles.newReports} />
                    <ConstructorItemsList
                        items={additionalReports}
                        rowType={EConstructorItem.REPORT}
                        title={tConstructorCommon.titles.additionalReports}
                    />
                    <InfiniteScroll
                        hasMore={templatesCount > templates.length}
                        initialLoad={false}
                        loadMore={getConstructorTemplateData}
                        threshold={0}
                    >
                        <ConstructorItemsList
                            items={templates}
                            rowType={EConstructorItem.TEMPLATE}
                            title={tConstructorCommon.titles.reportTemplates}
                        />
                    </InfiniteScroll>

                    <ConstructorFooter
                        subscribersInBasket={metaData.subscribersCount}
                        translation={tReportsConstructorTranslation}
                    />
                </ConstructorStyled>
            )}
        </Loader>
    );
};

export default PageReportsConstructor;
